// The analytics dashboard's decision logic, kept out of the component so it can
// be tested without a DOM. Counts come from d3-analytics through the Staff API's
// /analytics/* operations; every day here is a UTC day, YYYY-MM-DD.

// The sites d3-analytics counts that the dashboard offers. d3-analytics has no
// endpoint listing them, so this is the list: add a site here when it starts
// including d3-analytics's script.
export const ANALYTICS_SITES = ['vtt.dicedriven.com'];

// The preset ranges, in days ending today (UTC), today included.
export const RANGE_PRESETS = [7, 30, 90];
export const DEFAULT_RANGE_DAYS = 7;

// d3-analytics's limit on a range, in days, both ends included.
export const MAX_RANGE_DAYS = 366;

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A Date as its UTC day.
 * @param {Date} date
 * @returns {string}
 */
export function utcDay(date) {
    return date.toISOString().slice(0, 10);
}

/**
 * The range of `days` UTC days ending on `now`'s UTC day, inclusive.
 * @param {number} days
 * @param {Date} now
 * @returns {{from: string, to: string}}
 */
export function presetRange(days, now) {
    return {
        from: utcDay(new Date(now.getTime() - (days - 1) * DAY_MS)),
        to: utcDay(now),
    };
}

/**
 * Parses a UTC day, or null when it isn't a real one (2026-02-30 included).
 * @param {string} day
 * @returns {number|null} the day's midnight UTC, in milliseconds
 */
function parseDay(day) {
    if (!DAY_PATTERN.test(day ?? '')) return null;
    const ms = Date.parse(`${day}T00:00:00Z`);
    if (Number.isNaN(ms) || utcDay(new Date(ms)) !== day) return null;
    return ms;
}

/**
 * Why a custom range can't be asked for, or '' when it can. Mirrors
 * d3-analytics's own checks so staff hear about a bad range before a request.
 * @param {string} from
 * @param {string} to
 * @returns {string}
 */
export function validateRange(from, to) {
    const fromMs = parseDay(from);
    if (fromMs === null) return 'Pick a start day.';
    const toMs = parseDay(to);
    if (toMs === null) return 'Pick an end day.';
    if (toMs < fromMs) return "The end day can't be before the start day.";
    if ((toMs - fromMs) / DAY_MS + 1 > MAX_RANGE_DAYS) {
        return `The range can't be longer than ${MAX_RANGE_DAYS} days.`;
    }
    return '';
}

/**
 * The query string both reports take.
 * @param {string} site
 * @param {{from: string, to: string}} range
 * @returns {string}
 */
export function reportQuery(site, range) {
    return `?${new URLSearchParams({ site, from: range.from, to: range.to })}`;
}

/**
 * Page views per visitor, or null when there were no visitors.
 * @param {{visitors: number, pageViews: number}} totals
 * @returns {number|null}
 */
export function pageViewsPerVisitor(totals) {
    if (!totals.visitors) return null;
    return totals.pageViews / totals.visitors;
}

/**
 * Each source with its share of the sources' visitors (0–1), order kept
 * (d3-analytics sends them most first). Every visitor is counted under exactly
 * one source a day, so the shares sum to 1.
 * @param {Array<{source: string, visitors: number}>} sources
 * @returns {Array<{source: string, visitors: number, share: number}>}
 */
export function withShares(sources) {
    const total = sources.reduce((sum, s) => sum + s.visitors, 0);
    return sources.map((s) => ({ ...s, share: total ? s.visitors / total : 0 }));
}

/**
 * A share as a percentage, one decimal place.
 * @param {number} share
 * @returns {string}
 */
export function formatShare(share) {
    return `${(share * 100).toFixed(1)}%`;
}
